import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { 
  Info, GitBranch, Calendar, ChevronDown, ChevronRight,
  Bug, Sparkles, Wrench, Rocket, Tag, History
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AppVersion = () => {
  const { token } = useAuth();
  const [version, setVersion] = useState(null);
  const [buildDate, setBuildDate] = useState(null);
  const [changelog, setChangelog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedVersion, setExpandedVersion] = useState(null);

  useEffect(() => {
    fetchVersion();
  }, [token]);

  const fetchVersion = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/system/version`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setVersion(response.data.data.version);
        setBuildDate(response.data.data.build_date);
        setChangelog(response.data.data.changelog);
        // Expand first version by default
        if (response.data.data.changelog.length > 0) {
          setExpandedVersion(response.data.data.changelog[0].version);
        }
      }
    } catch (error) {
      console.error('Error fetching version:', error);
    } finally {
      setLoading(false);
    }
  };

  const getChangeTypeIcon = (type) => {
    const icons = {
      major: Rocket,
      feature: Sparkles,
      fix: Bug,
      improvement: Wrench
    };
    return icons[type] || Tag;
  };

  const getChangeTypeColor = (type) => {
    const colors = {
      major: 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400',
      feature: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
      fix: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400',
      improvement: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
    };
    return colors[type] || 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-400';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex flex-col items-center justify-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600 dark:border-indigo-400 mb-4"></div>
        <p className="text-gray-500 dark:text-gray-400">Memuat informasi versi...</p>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <div className="p-3 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl shadow-lg">
          <Info className="w-7 h-7 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100">Informasi Aplikasi</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">Versi dan riwayat pembaruan</p>
        </div>
      </div>

      {/* Current Version Card */}
      <Card className="bg-gradient-to-br from-indigo-500 to-purple-600 dark:from-indigo-600 dark:to-purple-700 border-0 shadow-xl">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white/20 backdrop-blur-sm rounded-2xl">
                <GitBranch className="w-8 h-8 text-white" />
              </div>
              <div>
                <p className="text-indigo-100 text-sm font-medium">Versi Aplikasi</p>
                <p className="text-3xl font-bold text-white">v{version}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-indigo-100 text-sm">ISP Billing System</p>
              <p className="text-white font-medium">RadiusLink</p>
              <p className="text-indigo-200 text-xs mt-1">
                Build: {buildDate}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Changelog */}
      <div>
        <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4 flex items-center gap-2">
          <History className="w-5 h-5" />
          Riwayat Pembaruan
        </h2>
        
        <div className="space-y-3">
          {changelog.map((release, index) => {
            const TypeIcon = getChangeTypeIcon(release.type);
            const isExpanded = expandedVersion === release.version;
            const isLatest = index === 0;
            
            return (
              <Card 
                key={release.version} 
                className={`border overflow-hidden transition-all duration-200 ${
                  isLatest 
                    ? 'border-indigo-300 dark:border-indigo-700 shadow-md' 
                    : 'border-gray-200 dark:border-gray-700'
                }`}
              >
                <div 
                  className="p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                  onClick={() => setExpandedVersion(isExpanded ? null : release.version)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {isExpanded ? (
                        <ChevronDown className="w-4 h-4 text-gray-400" />
                      ) : (
                        <ChevronRight className="w-4 h-4 text-gray-400" />
                      )}
                      
                      <span className={`px-2.5 py-1 rounded-lg text-sm font-bold ${
                        isLatest 
                          ? 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400' 
                          : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300'
                      }`}>
                        v{release.version}
                      </span>
                      
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium flex items-center gap-1 ${getChangeTypeColor(release.type)}`}>
                        <TypeIcon className="w-3 h-3" />
                        {release.type === 'major' ? 'Major' : 
                         release.type === 'feature' ? 'Feature' :
                         release.type === 'fix' ? 'Bug Fix' : 'Improvement'}
                      </span>
                      
                      {isLatest && (
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">
                          Terbaru
                        </span>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                      <Calendar className="w-4 h-4" />
                      {new Date(release.date).toLocaleDateString('id-ID', { 
                        day: 'numeric', 
                        month: 'long', 
                        year: 'numeric' 
                      })}
                    </div>
                  </div>
                </div>
                
                {isExpanded && (
                  <div className="px-4 pb-4 pt-0 border-t border-gray-100 dark:border-gray-800">
                    <ul className="space-y-2 mt-3">
                      {release.changes.map((change, idx) => (
                        <li 
                          key={idx} 
                          className="text-sm text-gray-700 dark:text-gray-300 flex items-start gap-2"
                        >
                          <span className="text-gray-400 mt-1">•</span>
                          <span>{change}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      </div>

      {/* Footer Info */}
      <Card className="bg-gray-50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700">
        <CardContent className="p-4">
          <div className="flex items-center gap-3 text-sm text-gray-600 dark:text-gray-400">
            <Info className="w-4 h-4" />
            <p>
              Jika Anda menemukan bug atau memiliki saran fitur, silakan hubungi administrator sistem.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AppVersion;
